import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, X, ArrowRight, Lock, CheckSquare, LayoutGrid, ListTodo, Users } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import ShareDialog from "../../components/os/ShareDialog";
import ItemMenu from "../../components/os/ItemMenu";
import OwnerFilter from "../../components/os/OwnerFilter";
import { useTeamFilter } from "../../hooks/useTeamFilter";
import { api, formatApiError } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import TrashDialog, { TrashButton } from "../../components/os/TrashDialog";

const ease = [0.16, 1, 0.3, 1];
const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors";
const AV = ["bg-violet", "bg-navy", "bg-emerald-500", "bg-gold", "bg-blue-600", "bg-rose-500"];
const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
const avColor = (id) => AV[(id || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % AV.length];
const COL_ES = { backlog: "Backlog", todo: "Por hacer", in_progress: "En curso", review: "Revisión", done: "Hecho" };

export default function OmniaFlow() {
  const { t, lang } = useI18n();
  const tf = useTeamFilter();
  const [metrics, setMetrics] = useState(null);
  const [projects, setProjects] = useState([]);
  const [modal, setModal] = useState(false);
  const [view, setView] = useState("projects");

  const load = useCallback(() => {
    api.get("/flow/overview").then((r) => setMetrics(r.data.metrics)).catch(() => {});
    api.get("/flow/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const [form, setForm] = useState({ name: "", description: "" });
  const [err, setErr] = useState("");
  const create = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post("/flow/projects", form); toast.success("OK"); setModal(false); setForm({ name: "", description: "" }); load(); }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Error"); }
  };

  const [share, setShare] = useState(null);
  const [trashOpen, setTrashOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", description: "" });
  const openEdit = (p) => { setEdit(p); setEditForm({ name: p.name, description: p.description || "" }); };
  const saveEdit = async (e) => {
    e.preventDefault();
    try { await api.patch(`/flow/projects/${edit.id}`, editForm); setEdit(null); toast.success("Proyecto actualizado"); load(); }
    catch (err2) { toast.error(formatApiError(err2.response?.data?.detail) || "Error"); }
  };
  const doDelete = async (p) => {
    if (!window.confirm(`¿Eliminar el proyecto "${p.name}"? Se moverá a la papelera (con tareas y archivos) y podrás restaurarlo durante 30 días.`)) return;
    try { await api.delete(`/flow/projects/${p.id}`); toast.success("Movido a la papelera (30 días)"); load(); }
    catch (err2) { toast.error(formatApiError(err2.response?.data?.detail) || "Error"); }
  };

  const VIEWS = [
    { key: "projects", icon: LayoutGrid },
    { key: "mine", icon: ListTodo },
    { key: "workload", icon: Users },
  ];

  return (
    <OSLayout title={t("os.nav.flow")}>
      {!metrics ? (
        <div className="text-navy/40">{t("os.loading")}</div>
      ) : (
        <div className="space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
              <div className="overline text-navy/40 mb-2">OMNIA · Flow</div>
              <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">Flow</h2>
              <p className="mt-2 text-navy/50">{t("os.flow.subtitle")}</p>
            </motion.div>
            <div className="flex items-center gap-3">
              <TrashButton testid="flow-trash-btn" onClick={() => setTrashOpen(true)} />
              <button data-testid="flow-new-project" onClick={() => setModal(true)}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90 transition-opacity">
                <Plus className="w-4 h-4" /> {t("os.flow.newProject")}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 border-b border-navy/10">
            <div className="flex gap-2">
            {VIEWS.map((v) => (
              <button key={v.key} data-testid={`flow-view-${v.key}`} onClick={() => setView(v.key)}
                className={`inline-flex items-center gap-2 px-4 py-3 text-sm -mb-px border-b-2 transition-colors ${view === v.key ? "border-violet text-navy" : "border-transparent text-navy/50 hover:text-navy"}`}>
                <v.icon className="w-4 h-4" /> {t(`os.flow.views.${v.key}`)}
              </button>
            ))}
            </div>
            <div className="pb-2"><OwnerFilter tf={tf} /></div>
          </div>

          {view === "projects" && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
                {metrics.map((m, i) => (
                  <motion.div key={m.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease, delay: i * 0.05 }} data-testid={`flow-kpi-${m.key}`}
                    className="bg-white border border-navy/10 rounded-lg p-5">
                    <div className="overline text-navy/40 text-[0.6rem]">{lang === "en" ? m.label_en : m.label_es}</div>
                    <div className="mt-2 text-2xl font-medium tracking-tight">{m.value}</div>
                  </motion.div>
                ))}
              </div>

              {projects.length === 0 ? (
                <div className="text-navy/40">{t("os.flow.empty")}</div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tf.filter(projects).map((p, i) => (
                    <motion.div key={p.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease, delay: i * 0.05 }} className="relative">
                      <div className="absolute top-4 right-4 z-10">
                        <ItemMenu testId={p.id} onInvite={() => setShare(p)} onEdit={() => openEdit(p)} onDelete={() => doDelete(p)} />
                      </div>
                      <Link to={`/os/flow/${p.id}`} data-testid={`flow-project-${p.id}`}
                        className="group block bg-white border border-navy/10 rounded-lg p-6 hover:border-violet/40 transition-colors h-full">
                        <div className="flex items-start justify-between">
                          <div className="w-2.5 h-2.5 rounded-full mt-1.5" style={{ background: p.color }} />
                          <ArrowRight className="w-4 h-4 text-navy/30 group-hover:text-violet group-hover:translate-x-1 transition-all mr-8" />
                        </div>
                        <h3 className="text-lg font-medium mt-3">{p.name}</h3>
                        <p className="text-sm text-navy/50 mt-1 line-clamp-2">{p.description}</p>
                        <div className="mt-4 flex items-center justify-between text-xs text-navy/40">
                          <span>{p.task_count} {t("os.flow.tasks")}</span>
                          <span>{t("os.flow.progress")} {p.progress}%</span>
                        </div>
                        <div className="mt-2 h-1.5 rounded-full bg-navy/5 overflow-hidden">
                          <div className="h-full bg-gradient-violet" style={{ width: `${p.progress}%` }} />
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}
            </>
          )}

          {view === "mine" && <MyTasksView t={t} colLabel={(s) => COL_ES[s] || s} />}
          {view === "workload" && <WorkloadView t={t} />}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={() => setModal(false)}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
            onClick={(e) => e.stopPropagation()} data-testid="flow-project-modal" className="w-full max-w-md bg-cream rounded-xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-medium">{t("os.flow.create.title")}</h3>
              <button onClick={() => setModal(false)} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <input required placeholder={t("os.flow.create.name")} data-testid="flow-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              <textarea rows={3} placeholder={t("os.flow.create.description")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} resize-none`} />
              {err && <p className="text-sm text-destructive">{err}</p>}
              <button data-testid="flow-project-submit" className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity">{t("os.flow.create.submit")}</button>
            </form>
          </motion.div>
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={() => setEdit(null)}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
            onClick={(e) => e.stopPropagation()} data-testid="flow-edit-modal" className="w-full max-w-md bg-cream rounded-xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-medium">Editar proyecto</h3>
              <button onClick={() => setEdit(null)} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveEdit} className="space-y-4">
              <input required data-testid="flow-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className={inputCls} />
              <textarea rows={3} data-testid="flow-edit-desc" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className={`${inputCls} resize-none`} />
              <button data-testid="flow-edit-submit" className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity">Guardar cambios</button>
            </form>
          </motion.div>
        </div>
      )}

      {share && <ShareDialog collection="flow_projects" id={share.id} title={share.name} onClose={() => setShare(null)} />}
      {trashOpen && <TrashDialog onClose={() => setTrashOpen(false)} onChanged={load} testid="flow-trash-dialog" />}
    </OSLayout>
  );
}

function MyTasksView({ t, colLabel }) {
  const [tasks, setTasks] = useState(null);
  useEffect(() => { api.get("/flow/my-tasks").then((r) => setTasks(r.data)).catch(() => setTasks([])); }, []);
  if (!tasks) return <div className="text-navy/40">…</div>;
  if (tasks.length === 0) return <div className="text-navy/40" data-testid="flow-mine-empty">{t("os.flow.mine.empty")}</div>;
  return (
    <div className="space-y-2" data-testid="flow-mine-list">
      {tasks.map((tk, i) => (
        <motion.div key={tk.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease, delay: i * 0.03 }}>
          <Link to={`/os/flow/${tk.project_id}`} data-testid={`flow-mine-${tk.id}`}
            className={`flex items-center gap-3 bg-white border border-navy/10 rounded-lg p-4 hover:border-violet/40 transition-colors ${tk.status === "done" ? "opacity-55" : ""}`}>
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: tk.project_color }} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate">{tk.title}</span>
                {tk.blocked && <span className="inline-flex items-center gap-1 text-[10px] text-gold bg-gold/10 px-1.5 py-0.5 rounded-full"><Lock className="w-2.5 h-2.5" />{t("os.flow.mine.blocked")}</span>}
              </div>
              <div className="text-xs text-navy/45 truncate">{tk.project_name}</div>
            </div>
            {tk.due_date && <span className="text-xs text-navy/40 shrink-0">{tk.due_date}</span>}
            <span className="text-[11px] px-2 py-1 rounded-full bg-navy/5 text-navy/50 shrink-0">{colLabel(tk.status)}</span>
          </Link>
        </motion.div>
      ))}
    </div>
  );
}

function WorkloadView({ t }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/flow/workload").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return <div className="text-navy/40">…</div>;
  if (rows.length === 0) return <div className="text-navy/40" data-testid="flow-workload-empty">{t("os.flow.workload.empty")}</div>;
  const max = Math.max(...rows.map((r) => r.total_open), 1);
  return (
    <div className="space-y-3" data-testid="flow-workload-list">
      {rows.map((r, i) => (
        <motion.div key={r.user_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease, delay: i * 0.04 }}
          data-testid={`flow-workload-${r.user_id}`} className="bg-white border border-navy/10 rounded-lg p-5">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 ${avColor(r.user_id)} rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0`}>{initials(r.name)}</div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">{r.name}</div>
              <div className="text-xs text-navy/45">{r.role}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-lg font-medium">{r.total_open}</div>
              <div className="text-[10px] text-navy/40">{t("os.flow.workload.open")}</div>
            </div>
          </div>
          <div className="mt-3 h-2 rounded-full bg-navy/5 overflow-hidden">
            <div className="h-full bg-gradient-violet" style={{ width: `${(r.total_open / max) * 100}%` }} />
          </div>
          <div className="mt-3 grid grid-cols-4 gap-2 text-center">
            {[["todo", r.todo], ["in_progress", r.in_progress], ["review", r.review], ["done", r.done]].map(([k, v]) => (
              <div key={k} className="bg-cream/60 rounded-md py-2">
                <div className="text-sm font-medium">{v}</div>
                <div className="text-[10px] text-navy/40">{t(`os.flow.workload.${k === "in_progress" ? "inProgress" : k}`)}</div>
              </div>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  );
}
